import { createBrowserRouter, Navigate, Outlet } from "react-router";
import { lazy } from "react";
import { InitialLayout } from "../layout/Initial.layout";
import NotFound from "../components/NotFound";
import { Routes } from "../types/enums/Routes.enum";
import { LoginLayout } from "@/modules/login/layout/Login.layout";
import { ValidacionLayout } from "@/modules/VU/layout/Validacion.layout";
import { ProtectedRoute } from "../components/ProtectedRoute";
import { PublicRoute } from "../components/PublicRoute";
import { PostLoginRolRoute } from "../components/PostLoginRolRoute";
import { LazyLoadWrapper, lazyWithRetry } from "../components/LazyLoadWrapper";
import { ProtectedAdminRoute } from "../components/ProtectedAdminRoute";
import { RegistrationFilesProvider } from "@/modules/login/contexts/RegistrationFilesContext";

/*
 * LAZY LOADED PAGES - Implementación de Code Splitting
 * Los componentes se cargan solo cuando son necesarios, reduciendo el bundle inicial
 */

/* Módulo de Login - Carga prioritaria (se mantienen algunos críticos) */
const LoginPage = lazy(() => import("@/modules/login/pages/Login.page"));
const ForgotPasswordPage = lazy(() => import("@/modules/login/pages/ForgotPassword.page"));
const CredencialesForgotPassword = lazy(() => import("@/modules/login/pages/CredencialesForgotPassword"));
const PostLoginRolPage = lazyWithRetry(() => import("@/modules/login/pages/PostLoginRol.page"), 'circular');

/* Módulo de Registro - Lazy load completo */
const RegistroPage = lazyWithRetry(() => import("@/modules/login/pages/Registro.page"), 'minimal');
const Identificacion = lazy(() => import("@/modules/login/pages/Identificacion.page"));
const Credenciales = lazy(() => import("@/modules/login/pages/Credenciales.page"));
const DatosProfesionales = lazy(() => import("@/modules/login/pages/DatosProfesionales.page"));
const TerminosCondicionesPage = lazy(() => import("@/modules/login/pages/TerminosCondiciones.page"));
const IdentificacionEntidad = lazy(() => import("@/modules/login/pages/IdentificacionEntidad.page"));
const DatosEntidad = lazy(() => import("@/modules/login/pages/DatosEntidad.page"));
const DocumentosEntidad = lazy(() => import("@/modules/login/pages/DocumentosEntidad.page"));
const ContactosEntidad = lazy(() => import("@/modules/login/pages/ContactosEntidad.page"));

/* Módulo principal - Escritorio (carga temprana) */
const Escritorio = lazyWithRetry(() => import("@/modules/escritorio/pages/Escritorio.page"), 'circular');

/* Módulo de Casos - Heavy module, lazy load completo */
const Casos = lazy(() => import("@/modules/casos/pages/Casos.page"));
const VerTramites = lazyWithRetry(() => import("@/modules/casos/pages/VerTramites.page"), 'circular');
const CasosForm = lazy(() => import("@/modules/casos/pages/CasosForm.page"));
const DetallesTramite = lazy(() => import("@/modules/casos/pages/DetallesTramite.page"));

/* Módulos con librerías pesadas - Lazy load crítico */
const CalendarPage = lazy(() => import("@/modules/calendar/pages/Calendar.page")); // FullCalendar
const AudienciaPage = lazy(() => import("@/modules/audiencias/pages/Audiencias.page"));

/* Otros módulos */
const FilePage = lazyWithRetry(() => import("@/modules/Archivo").then(m => ({ default: m.FilePage })), 'linear');
const BuzonJudicialPage = lazy(() => import("@/modules/buzon-judicial").then(m => ({ default: m.BuzonJudicialPage })));
const EquipoPage = lazy(() => import("@/modules/equipo").then(m => ({ default: m.EquipoPage })));
const ValidarUsuarioPage = lazy(() => import("@/modules/VU/pages/ValidacionUsuario.page"));

/* Módulo de Seguridad */
const DeviceConfirmationPage = lazy(() => import("@/modules/security/pages/DeviceConfirmation.page"));
const MyDevicesPage = lazy(() => import("@/modules/security/pages/Devices.page"));

/* Componente wrapper para rutas protegidas que requieren layout */
const ProtectedLayoutWrapper = ({
    layout: Layout,
    children,
    permissions,
    ...routeProps
}: {
    layout: React.ComponentType<{ children: React.ReactNode }>;
    children: React.ReactNode;
    permissions?: Array<{ modulo: string; permiso?: string }>;
    [key: string]: any;
}) => (
    <ProtectedRoute
        requiredPermissions={permissions}
        showAccessDenied={true}
        accessDeniedVariant="detailed"
        loadingVariant="pulsing"
        {...routeProps}
    >
        <Layout>{children}</Layout>
    </ProtectedRoute>
);

/* Componente para rutas de módulos protegidos */
const ProtectedModuleRoute = ({
    children,
    permissions,
    variant = "default",
    showContact = false,
    loading = "circular"
}: {
    children: React.ReactNode;
    permissions: Array<{ modulo: string; permiso?: string }>;
    variant?: "default" | "minimal" | "detailed";
    showContact?: boolean;
    loading?: "circular" | "linear" | "pulsing" | "dots";
}) => (
    <ProtectedRoute
        requiredPermissions={permissions}
        showAccessDenied={true}
        accessDeniedVariant={variant}
        showContactInfo={showContact}
        loadingVariant={loading}
    >
        {children}
    </ProtectedRoute>
);

/* Componente wrapper para rutas públicas que redirigen si está autenticado */
const PublicLayoutWrapper = ({
    layout: Layout,
    children
}: {
    layout: React.ComponentType<{ children: React.ReactNode }>;
    children: React.ReactNode;
}) => (
    <PublicRoute>
        <Layout>{children}</Layout>
    </PublicRoute>
);

/**
 * Crea y configura el router de la aplicación.
 * Esta función se llama dentro del árbol de providers para garantizar
 * que el AuthContext esté disponible cuando los componentes de ruta se evalúen.
 */
export const createRouter = () => createBrowserRouter([
    /* Rutas públicas de autenticación (redirigen si ya está autenticado) */
    {
        path: Routes.LOGIN,
        element: (
            <PublicLayoutWrapper layout={LoginLayout}>
                <Outlet />
            </PublicLayoutWrapper>
        ),
        errorElement: <NotFound />,
        children: [
            {
                index: true,
                element: (
                    <LazyLoadWrapper variant="minimal">
                        <LoginPage />
                    </LazyLoadWrapper>
                )
            },
            {
                path: Routes.VALIDATE_FOR_PASSWORD,
                element: (
                    <LazyLoadWrapper variant="minimal">
                        <ForgotPasswordPage />
                    </LazyLoadWrapper>
                )
            },
            {
                path: Routes.FORGOT_PASSWORD,
                element: (
                    <LazyLoadWrapper variant="minimal">
                        <CredencialesForgotPassword />
                    </LazyLoadWrapper>
                )
            },
        ]
    },

    /* Ruta de selección de rol post-login (accesible solo con autenticación básica) */
    {
        path: Routes.POST_LOGIN_ROL,
        element: (
            <PostLoginRolRoute>
                <LoginLayout />
            </PostLoginRolRoute>
        ),
        errorElement: <NotFound />,
        children: [
            {
                index: true,
                element: <PostLoginRolPage />
            }
        ]
    },

    /* Rutas públicas de registro (redirigen si ya está autenticado) */
    {
        path: Routes.REGISTRO,
        element: (
            <PublicLayoutWrapper layout={LoginLayout}>
                <Outlet />
            </PublicLayoutWrapper>
        ),
        errorElement: <NotFound />,
        children: [
            {
                path: Routes.REGISTRO_PERSONA,
                element: <RegistroPage />
            },
            {
                path: ":tipo/:subtipo",
                children: [
                    {
                        index: true,
                        element: <Navigate to="identificacion" replace />
                    },
                    {
                        path: "identificacion",
                        element: (
                            <LazyLoadWrapper variant="minimal">
                                <Identificacion />
                            </LazyLoadWrapper>
                        )
                    },
                    {
                        path: "credenciales",
                        element: (
                            <LazyLoadWrapper variant="minimal">
                                <Credenciales />
                            </LazyLoadWrapper>
                        )
                    },
                    {
                        path: "datos-profesionales",
                        element: (
                            <LazyLoadWrapper variant="minimal">
                                <DatosProfesionales />
                            </LazyLoadWrapper>
                        )
                    },
                    // {
                    //     path: "otp",
                    //     element: <OTP />
                    // },
                    {
                        path: "terminos-condiciones",
                        element: (
                            <LazyLoadWrapper variant="minimal">
                                <TerminosCondicionesPage />
                            </LazyLoadWrapper>
                        )
                    }
                ]
            },
            {
                path: "pj/:subtipo",
                element: (
                    <RegistrationFilesProvider>
                        <Outlet />
                    </RegistrationFilesProvider>
                ),
                children: [
                    {
                        index: true,
                        element: <Navigate to="identificacion" replace />
                    },
                    {
                        path: "identificacion",
                        element: (
                            <LazyLoadWrapper variant="minimal">
                                <IdentificacionEntidad />
                            </LazyLoadWrapper>
                        )
                    },
                    {
                        path: "datos",
                        element: (
                            <LazyLoadWrapper variant="minimal">
                                <DatosEntidad />
                            </LazyLoadWrapper>
                        )
                    },
                    {
                        path: "documentos",
                        element: (
                            <LazyLoadWrapper variant="minimal">
                                <DocumentosEntidad />
                            </LazyLoadWrapper>
                        )
                    },
                    {
                        path: "contactos",
                        element: (
                            <LazyLoadWrapper variant="minimal">
                                <ContactosEntidad />
                            </LazyLoadWrapper>
                        )
                    },
                    {
                        path: "terminos-condiciones",
                        element: (
                            <LazyLoadWrapper variant="minimal">
                                <TerminosCondicionesPage />
                            </LazyLoadWrapper>
                        )
                    }
                ]
            }
        ]
    },

    /* Ruta pública de validación VU con query params (token, email, id) */
    {
        path: `${Routes.VU}/validacion`,
        element: <ValidacionLayout />,
        errorElement: <NotFound />,
        children: [
            {
                index: true,
                element: (
                    <LazyLoadWrapper variant="circular">
                        <ValidarUsuarioPage />
                    </LazyLoadWrapper>
                )
            }
        ]
    },

    /* Ruta pública de confirmación de dispositivo */
    {
        path: '/device/confirm/:token',
        element: (
            <LazyLoadWrapper variant="circular">
                <DeviceConfirmationPage />
            </LazyLoadWrapper>
        ),
        errorElement: <NotFound />
    },

    /* Rutas protegidas principales con layout */
    {
        path: Routes.DEFAULT,
        element: (
            <ProtectedLayoutWrapper
                layout={InitialLayout}
                permissions={[]}
            >
                <Outlet />
            </ProtectedLayoutWrapper>
        ),
        errorElement: <NotFound />,
        children: [
            {
                index: true,
                element: <Escritorio />
            },
            /* Módulo de Casos Judiciales */
            {
                path: "caso",
                children: [
                    {
                        index: true,
                        element: (
                            <ProtectedModuleRoute
                                permissions={[{ modulo: 'caso', permiso: 'ver' }]}
                                variant="minimal"
                                showContact={true}
                                loading="circular"
                            >
                                <LazyLoadWrapper variant="circular">
                                    <Casos />
                                </LazyLoadWrapper>
                            </ProtectedModuleRoute>
                        )
                    },
                    {
                        path: "ver-tramites",
                        element: (
                            <ProtectedModuleRoute
                                permissions={[{ modulo: 'caso', permiso: 'ver' }]}
                                variant="minimal"
                                loading="circular"
                            >
                                <VerTramites />
                            </ProtectedModuleRoute>
                        )
                    },
                    {
                        path: "detalles-tramite/:id",
                        element: (
                            <ProtectedModuleRoute
                                permissions={[{ modulo: 'caso', permiso: 'ver' }]}
                                variant="minimal"
                                loading="circular"
                            >
                                <div className="p-8 text-center">
                                    <h1 className="text-2xl font-bold text-gray-800">
                                        Detalles del Trámite
                                    </h1>
                                    <p className="text-gray-600 mt-2">
                                        Esta funcionalidad está en desarrollo
                                    </p>
                                </div>
                            </ProtectedModuleRoute>
                        )
                    },
                    {
                        path: ":casoId/tramites/:tramiteId",
                        element: (
                            <ProtectedModuleRoute
                                permissions={[{ modulo: 'caso', permiso: 'ver' }]}
                                variant="minimal"
                                loading="circular"
                            >
                                <LazyLoadWrapper variant="circular">
                                    <DetallesTramite/>
                                </LazyLoadWrapper>
                            </ProtectedModuleRoute>
                        )
                    },
                    {
                        path: "crear",
                        element: (
                            <ProtectedModuleRoute
                                permissions={[{ modulo: 'caso', permiso: 'ver' }]}
                                variant="minimal"
                                loading="circular"
                            >
                                <LazyLoadWrapper variant="circular">
                                    <CasosForm/>
                                </LazyLoadWrapper>
                            </ProtectedModuleRoute>
                        )
                    },                  
                ]
            },
            /* Módulo de audiencia */
            {
                path: Routes.AUDIENCIAS,
                element: (
                    <ProtectedModuleRoute
                        permissions={[{ modulo: 'audiencia', permiso: 'ver' }]}
                        variant="minimal"
                        loading="circular"
                    >
                        <LazyLoadWrapper variant="circular">
                            <AudienciaPage />
                        </LazyLoadWrapper>
                    </ProtectedModuleRoute>
                )
            },
            /* Módulo de Calendario */
            {
                path: Routes.CALENDARIO,
                element: (
                    <ProtectedModuleRoute
                        permissions={[{ modulo: 'calendario', permiso: 'ver' }]}
                        variant="minimal"
                        loading="circular"
                    >
                        <LazyLoadWrapper variant="circular">
                            <CalendarPage/>
                        </LazyLoadWrapper>
                    </ProtectedModuleRoute>
                )
            },
            /* Módulo de Archivo */
            {
                path: Routes.ARCHIVO,
                element: (
                    <ProtectedModuleRoute
                        permissions={[{ modulo: 'archivo', permiso: 'ver' }]}
                        variant="minimal"
                        showContact={true}
                        loading="linear"
                    >
                        <FilePage />
                    </ProtectedModuleRoute>
                )
            },
            /* Módulo de Buzón Judicial */
            {
                path: Routes.BUZON_JUDICIAL,
                element: (
                    <ProtectedModuleRoute
                        permissions={[]}
                        variant="minimal"
                        loading="circular"
                    >
                        <LazyLoadWrapper variant="circular">
                            <BuzonJudicialPage />
                        </LazyLoadWrapper>
                    </ProtectedModuleRoute>
                )
            },
            /* Módulo de Equipo */
            {
                path: Routes.EQUIPO,
                element: (
                    <ProtectedModuleRoute
                        permissions={[{ modulo: 'equipo', permiso: 'ver' }]}
                        variant="minimal"
                        loading="circular"
                    >
                        <ProtectedAdminRoute>
                            <LazyLoadWrapper variant="circular">
                                <EquipoPage />
                            </LazyLoadWrapper>
                        </ProtectedAdminRoute>
                    </ProtectedModuleRoute>
                )
            },
            /* Módulo de Seguridad - Gestión de Dispositivos */
            {
                path: 'settings/devices',
                element: (
                    <LazyLoadWrapper variant="circular">
                        <MyDevicesPage />
                    </LazyLoadWrapper>
                )
            }
        ]
    },

    /* Rutas de validación de usuario */
    {
        path: Routes.VU,
        element: (
            <ProtectedLayoutWrapper
                layout={ValidacionLayout}
                permissions={[{ modulo: 'validacion' }]}
                showContactInfo={true}
                accessDeniedVariant="detailed"
            >
                <Outlet />
            </ProtectedLayoutWrapper>
        ),
        errorElement: <NotFound />,
        children: [
            {
                index: true,
                element: <Navigate to="validacion-usuario" replace />
            },
            {
                path: "validacion-usuario",
                element: (
                    <LazyLoadWrapper variant="circular">
                        <ValidarUsuarioPage />
                    </LazyLoadWrapper>
                )
            }
        ]
    },

    /* Ruta catch-all para páginas no encontradas */
    {
        path: "*",
        element: <NotFound />
    }
]);

/**
 * Router exportado para compatibilidad (deprecated)
 * @deprecated Use createRouter() dentro del árbol de providers
 */
export const router = createRouter();